import React, { createContext, useContext, useEffect, useState } from "react";
import { Language } from "@/components/edit/captions/CaptionEditorUtil";
import { getLanguageInfoByCode } from "@/lib/LanguageUtils";
import { AudioTrack, Segment, Transcription, Voice } from "@/lib/types";
import { listTranscriptsByUserVideoId, mapTranscriptionFromFirestore, saveTranscriptions, subscribeToTranscripts } from "@/lib/Transcript";
import { listAudioTracksForVideo } from "@/lib/AudioTrack";
export interface CaptionEditorContextInterface {
  transcriptions: Map<string, Transcription>;
  setTranscriptions: (newState: Map<string, Transcription> | ((prevState: Map<string, Transcription>) => Map<string, Transcription>)) => void;
  languages: Language[];
  enableRegenerateButton: boolean;
  selectedSourceLanguage: string;
  selectedTargetLanguage: string;
  setSelectedTargetLanguage: (language: string) => void;
  successMessage: string;
  errorMessage: string;
  handleSourceLanguageCaptionChange: (index: number, field: keyof Segment, value: string) => void;
  handleTargetLanguageCaptionChange: (index: number, field: keyof Segment, value: string) => void;
  onCaptionsChanged?: () => void;
  setLanguageCodeForTranscriptionWithChanges: (newState: (prev: Set<string>) => Set<string>) => void;
  setSaveCaptionsAndSpeakersTriggered: (triggered: boolean) => void;
  setRegenerateDecision: (regenerate: boolean) => void;
  setSelectedTimestamp: (timestamp: number) => void;
  selectedTimestamp: number;
}
export const CaptionEditorContext = createContext<CaptionEditorContextInterface>({
  transcriptions: new Map(),
  setTranscriptions: () => {},
  languages: [],
  enableRegenerateButton: false,
  selectedSourceLanguage: "",
  selectedTargetLanguage: "",
  setSelectedTargetLanguage: () => {},
  successMessage: "",
  errorMessage: "",
  handleSourceLanguageCaptionChange: () => {},
  handleTargetLanguageCaptionChange: () => {},
  onCaptionsChanged: () => {},
  setLanguageCodeForTranscriptionWithChanges: () => {},
  setSaveCaptionsAndSpeakersTriggered: () => {},
  setRegenerateDecision: () => {},
  setSelectedTimestamp: () => {},
  selectedTimestamp: 0
});
export const CaptionEditorContextProvider = ({
  children,
  userVideoId,
  setSaveCaptionAndSpeakerCallback,
  onCaptionsChanged
}: {
  children: React.ReactNode;
  userVideoId: string;
  setSaveCaptionAndSpeakerCallback?: (callback: () => void) => void;
  onCaptionsChanged?: () => void;
}) => {
  const [transcriptions, setTranscriptions] = React.useState<Map<string, Transcription>>(new Map());
  const [languageCodeForTranscriptionWithChanges, setLanguageCodeForTranscriptionWithChanges] = React.useState<Set<string>>(new Set());
  const [audioTracks, setAudioTracks] = React.useState<AudioTrack[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [selectedSourceLanguage, setSelectedSourceLanguage] = useState("");
  const [selectedTargetLanguage, setSelectedTargetLanguage] = useState("");
  const [enableRegenerateButton, setEnableRegenerateButton] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [saveCaptionsTriggered, setSaveCaptionsAndSpeakersTriggered] = useState(false);
  const [regenerateDecision, setRegenerateDecision] = useState(false);
  const [selectedTimestamp, setSelectedTimestamp] = useState(0);
  useEffect(() => {
    if (!userVideoId) {
      return;
    }
    let unsubscribe: () => void;
    const handleSnapshot = (snapshot: any) => {
      if (snapshot.empty) {
        return;
      }

      // Use the functional update form to have the most recent transcriptions and languages states
      setTranscriptions(prevTranscriptions => {
        const newTranscriptions = new Map(prevTranscriptions);
        setLanguages(prevLanguages => {
          const updatedLanguages = [...prevLanguages];
          snapshot.docChanges().forEach((change: any) => {
            if (change.type === "added") {
              const newTranscript = mapTranscriptionFromFirestore(change.doc) as Transcription;
              const {
                language
              } = newTranscript;

              // Add the new transcription to the map
              newTranscriptions.set(language, newTranscript);

              // Update the source language if it's not set or if the new transcript is the source
              if (newTranscript.isSourceTranscript && (!selectedSourceLanguage || selectedSourceLanguage != newTranscript.language)) {
                setSelectedSourceLanguage(language);
              }

              // Check if the language is already in the list before adding
              if (!updatedLanguages.find(lang => lang.code === language)) {
                const languageInfo = getLanguageInfoByCode(language);
                updatedLanguages.push({
                  code: language,
                  label: languageInfo?.label || language
                });
              }
            }
          });
          return updatedLanguages;
        });
        return newTranscriptions;
      });
    };
    async function subscribe() {
      unsubscribe = await subscribeToTranscripts(userVideoId, handleSnapshot);
    }
    subscribe().then(() => {
      console.log('Subscribed to Transcripts');
    });
    return () => {
      unsubscribe && unsubscribe();
    };
  }, [userVideoId]);
  useEffect(() => {
    fetchAllTracks();
  }, [userVideoId]);
  useEffect(() => {
    if (selectedSourceLanguage) {
      const audioTrack = audioTracks.find(track => track.languageCode === selectedSourceLanguage);
      if (audioTrack) {
        setEnableRegenerateButton(true);
      } else {
        setEnableRegenerateButton(false);
      }
    }
    setSuccessMessage("");
    setErrorMessage("");
  }, [audioTracks, selectedSourceLanguage]);
  useEffect(() => {
    // Set a language other than the selected source language as the target language
    if (languages.length > 1) {
      if (!selectedTargetLanguage) {
        const targetLanguage = languages.find(language => language.code !== selectedSourceLanguage);
        if (targetLanguage) {
          setSelectedTargetLanguage(targetLanguage.code);
        }
      }
    }
  }, [languages, selectedSourceLanguage]);
  const fetchAllTracks = () => {
    Promise.all([fetchAudioTracks(), fetchTranscripts()]).then(([audioTracks, transcripts]) => {
      const audioLanguages = audioTracks ? audioTracks.map(track => {
        return {
          label: getLanguageInfoByCode(track.languageCode)?.label || track.label,
          code: track.languageCode
        };
      }) : [];

      // Get the transcript languages
      const transcriptLanguages = transcripts ? Array.from(transcripts.keys()).map(languageCode => {
        return {
          label: getLanguageInfoByCode(languageCode)?.label || languageCode,
          code: languageCode
        };
      }) : [];
      const combinedTrackLanguages = [...audioLanguages, ...transcriptLanguages];
      const filteredLanguages = Array.from(new Map(combinedTrackLanguages.map(track => [track.code, track])).values());
      setLanguages(filteredLanguages);
    }).catch(error => {
      console.error('Error getting tracks', error);
    });
  };
  const fetchTranscripts = (): Promise<Map<string, Transcription> | void> => {
    return listTranscriptsByUserVideoId(userVideoId).then(transcripts => {
      const transcriptions = new Map(transcripts.map(transcript => [transcript.language, transcript]));
      setTranscriptions(transcriptions);
      return transcriptions;
    }).catch(error => {
      console.error('Error getting transcripts', error);
    });
  };

  // Audio tracks are used to determine if the regenerate button should be enabled
  const fetchAudioTracks = (): Promise<AudioTrack[] | void> => {
    return listAudioTracksForVideo(userVideoId).then(tracks => {
      setAudioTracks(tracks);
      const defaultTrack = tracks.find(track => track.defaultTrack === true);
      if (defaultTrack) {
        setSelectedSourceLanguage(defaultTrack.languageCode);
      }
      return tracks;
    }).catch(error => {
      console.error('Error getting audio tracks', error);
    });
  };
  const handleSourceLanguageCaptionChange = (index: number, field: keyof Segment, value: string) => {
    // Sets messages to empty again when the user starts typing on the same selected language
    setErrorMessage("");
    setSuccessMessage("");
    setTranscriptions(prevTranscriptions => {
      const updatedTranscriptions = new Map(prevTranscriptions);
      const sourceTranscription = updatedTranscriptions.get(selectedSourceLanguage);
      if (sourceTranscription) {
        const updatedSegment = {
          ...sourceTranscription.segments[index],
          [field]: value
        };
        if (field === "text") {
          updatedSegment.textUpdated = true;
        }
        sourceTranscription.segments[index] = updatedSegment;
        onCaptionsChanged && onCaptionsChanged();
      }
      return updatedTranscriptions;
    });
    setLanguageCodeForTranscriptionWithChanges(prev => new Set(prev).add(selectedSourceLanguage));
  };
  const handleTargetLanguageCaptionChange = (index: number, field: keyof Segment, value: string) => {
    setTranscriptions(prevTranscriptions => {
      const updatedTranscriptions = new Map(prevTranscriptions);
      const targetTranscription = updatedTranscriptions.get(selectedTargetLanguage);
      if (targetTranscription) {
        const updatedSegment = {
          ...targetTranscription.segments[index],
          [field]: value
        };
        if (field === "text") {
          updatedSegment.textUpdated = true;
        }
        targetTranscription.segments[index] = updatedSegment;
        onCaptionsChanged && onCaptionsChanged();
      }
      return updatedTranscriptions;
    });
    setLanguageCodeForTranscriptionWithChanges(prev => new Set(prev).add(selectedTargetLanguage));
  };
  const handleSaveCaptionsAndSpeakers = () => {
    setSaveCaptionsAndSpeakersTriggered(true);
  };
  useEffect(() => {
    if (saveCaptionsTriggered) {
      // Filter out the modified transcriptions
      const filteredTranscriptions = new Map(Array.from(transcriptions.entries()).filter(([languageCode]) => languageCodeForTranscriptionWithChanges.has(languageCode)));
      if (filteredTranscriptions.size > 0) {
        saveTranscriptions(userVideoId, filteredTranscriptions, regenerateDecision);
        setSuccessMessage("Changes saved successfully.");
      } else {
        console.log('No changes to save.');
      }

      // Reset the tracking variables after saving
      setSaveCaptionsAndSpeakersTriggered(false);
      setLanguageCodeForTranscriptionWithChanges(new Set());
      if (regenerateDecision) {
        // Set attributes back to false for modified transcripts (for display purposes)
        setTranscriptions(prevTranscriptions => {
          const updatedTranscriptions = new Map(prevTranscriptions);
          filteredTranscriptions.forEach((_, languageCode) => {
            const transcription = updatedTranscriptions.get(languageCode);
            if (transcription) {
              transcription.segments.forEach(segment => {
                if (segment.textUpdated === true) segment.textUpdated = false;
                if (segment.speakerUpdated === true) segment.speakerUpdated = false;
              });
              transcription.speakers.forEach(speaker => {
                if (speaker.labelUpdated === true) speaker.labelUpdated = false;
                if (speaker.voiceUpdated === true) speaker.voiceUpdated = false;
              });
            }
          });
          return updatedTranscriptions;
        });
      }
    }
  }, [saveCaptionsTriggered]);
  useEffect(() => {
    setSaveCaptionAndSpeakerCallback && setSaveCaptionAndSpeakerCallback(() => handleSaveCaptionsAndSpeakers);
  }, [setSaveCaptionAndSpeakerCallback]);
  return <CaptionEditorContext.Provider value={{
    transcriptions,
    setTranscriptions,
    languages,
    enableRegenerateButton,
    selectedSourceLanguage,
    selectedTargetLanguage,
    setSelectedTargetLanguage,
    successMessage,
    errorMessage,
    handleSourceLanguageCaptionChange,
    handleTargetLanguageCaptionChange,
    onCaptionsChanged,
    setLanguageCodeForTranscriptionWithChanges,
    setSaveCaptionsAndSpeakersTriggered,
    setRegenerateDecision,
    setSelectedTimestamp,
    selectedTimestamp
  }} data-sentry-element="unknown" data-sentry-component="CaptionEditorContextProvider" data-sentry-source-file="CaptionEditorContext.tsx">
            {children}
        </CaptionEditorContext.Provider>;
};
export const useCaptionEditorContext = () => useContext(CaptionEditorContext);